import type { ReactNode } from "react";
import { EmptyState, ErrorState, Skeleton } from "./states.js";

export interface Column<T> {
  key: string;
  header: string;
  render: (row: T) => ReactNode;
  className?: string;
}

export interface DataTableProps<T> {
  caption: string;
  columns: Column<T>[];
  rows: T[];
  rowKey: (row: T) => string;
  loading?: boolean;
  error?: { message: string; requestId?: string } | null;
  onRetry?: () => void;
  emptyTitle?: string;
  emptyDescription?: string;
  emptyAction?: ReactNode;
}

export function DataTable<T>({
  caption,
  columns,
  rows,
  rowKey,
  loading = false,
  error = null,
  onRetry,
  emptyTitle = "Nothing here yet",
  emptyDescription = "Records will appear once work is created.",
  emptyAction,
}: DataTableProps<T>) {
  return (
    <section aria-label={caption}>
      {loading ? <Skeleton rows={4} /> : null}
      {!loading && error ? (
        <ErrorState
          message={error.message}
          requestId={error.requestId}
          onRetry={onRetry}
        />
      ) : null}
      {!loading && !error && rows.length === 0 ? (
        <EmptyState
          title={emptyTitle}
          description={emptyDescription}
          action={emptyAction}
        />
      ) : null}
      {!loading && !error && rows.length > 0 ? (
        <div className="overflow-x-auto rounded-[var(--radius-card)] border border-line bg-surface-raised">
          <table className="w-full min-w-[560px] border-collapse text-sm">
            <caption className="sr-only">{caption}</caption>
            <thead>
              <tr className="border-b border-line bg-surface-inset text-start">
                {columns.map((column) => (
                  <th
                    key={column.key}
                    scope="col"
                    className="px-3 py-2.5 font-medium text-ink-muted"
                  >
                    {column.header}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {rows.map((row) => (
                <tr key={rowKey(row)} className="border-b border-line/60 last:border-b-0">
                  {columns.map((column) => (
                    <td
                      key={column.key}
                      className={column.className ?? "px-3 py-2.5 align-middle"}
                    >
                      {column.render(row)}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : null}
    </section>
  );
}
